import { find, get, has } from 'lodash';

import {
  getAnswerCondition,
  updateState,
  getTaxonomyCondition,
  getConceptCondition,
  getLogicKeyFromMode,
  getVisibilityCondition,
} from 'containers/QuestionnaireLogic/utils';

import {
  getDefaultConditionAnswer,
  getDefaultConditionVisibility,
} from 'containers/NodeLogic/utils';
import {
  getDefaultMatcherForVisibility,
  getDefaultMatcherForConcept,
  getDefaultMatcherForDemographic,
  getDefaultMatcherForQuestionType,
  getDefaultMatcherForQuota,
  getDefaultMatcherForVariable,
  isNumericQuestionType,
  isTextQuestionType,
} from 'components/LogicMatcher/utils';

import {
  Statement,
  Rule,
  Outcome,
  OutcomeType,
  Operator,
  NextNode,
  QuestionnaireConditionContext,
  conditionService,
  questionnaireLogicService,
  FinalNode,
  ActionVisibilityValueType,
  OutcomeActionType,
  QuotaConditionTypeEnum,
  QuotaConditionContext,
} from 'utils/adl-services';

import { getVariableCondition } from 'modules/HiddenVariables/adl-utils';
import { getDefaultStatementWithOutcomeAction } from 'components/LogicOutcomeWithActions/utils';

import {
  GROUP_LOOP_MERGE,
  NODE_TYPES_SUPPORT_SIMPLE_QUOTAS,
} from 'data/nodeTypes';
import {
  isNumericOpenEndedPeeqQuestionType,
  isOpenEndedPeeqQuestionType,
} from 'utils/nodeTypes';
import findQuestionByRefId from 'utils/findQuestionByRefId';
import { QUOTA_LOGIC_DEFAULT_VALUE } from 'components/LogicQuota/constants';
import { REASON_QUESTIONNAIRE_QUOTA_FULL } from 'components/LogicOutcome/constants';
import { addQuotaRulesForSpecificAnswersColumns } from 'utils/quotaHelpers';
import { qlKeepScreeningRules } from './screening';
import {
  getCarriedForwardedOptionsByRefId,
  qlRemoveLogicOfItemAndCopies,
} from './carriedForwardCopies';
import {
  getRevalidatedQuestionsAndGlobalErrors,
  mergePreviousNodesPerNode,
  getSurveyConfigForValidate,
  getNodeErrors,
  updateQuestionnaireErrors,
  getRevalidatedNodesAndGlobalErrors,
  getNodesWithoutMentionsOnRemovedNode,
  validateQuotaAndUpdateState,
  getQuotaEligibleOptions,
  getQuotaEligibilityForNode,
  getUnconditionallyHiddenItems,
} from './utils';

import { LOGIC_MODES, DISQUALIFY_PLACEHOLDER_QUESTION } from './constants';
import { HIDE_ANSWER_LOGIC_MODE } from './UnconditionalHideAnswer/constants';
import { makeSelectQuestionQuotasMinimalMap } from './selectorUtils/quotasMinimal';

function generateStatementFromAction(action) {
  const {
    nodeRefId,
    matcher,
    isNumeric = false,
    isText = false,
    answerRefId,
    nextQuestionRefId,
    context,
    contextType,
    outcomeType = OutcomeType.Success,
    outcomeAction,
    hasRule = true,
    logicMode,
  } = action.payload;

  let outcomeReason = null;
  let condition;
  if (!contextType) {
    condition = conditionService.createCondition(null, null, null);
  } else if (contextType === QuestionnaireConditionContext.Answer) {
    condition = getAnswerCondition({
      questionRefId: nodeRefId,
      matcher,
      isNumeric,
      isText,
      defaultValue: answerRefId || null,
      context,
    });
  }

  const rule = hasRule
    ? new Rule({
        operator: Operator.Any,
        conditions: [condition],
      })
    : undefined;

  if (logicMode === LOGIC_MODES.ADVANCED_QUOTA_LOGIC) {
    rule.quota = conditionService.createCondition(
      QUOTA_LOGIC_DEFAULT_VALUE,
      new QuotaConditionContext(rule.id, QuotaConditionTypeEnum.Advanced),
      getDefaultMatcherForQuota(),
    );
    outcomeReason = REASON_QUESTIONNAIRE_QUOTA_FULL;
  }

  const statement = new Statement({
    rule,
    outcome: generateOutcome({
      outcomeType,
      outcomeAction,
      nextQuestionRefId,
      outcomeReason,
    }),
  });

  return { rule, statement };
}

export function addStatementActionHandler(state, action) {
  const { nodeRefId, logicMode = LOGIC_MODES.PLAIN, scrollTo } = action.payload;
  const logicKey = getLogicKeyFromMode(logicMode);
  const logicDomain = state[logicKey];
  const currentQl = logicDomain.ql;

  try {
    const { statement } = generateStatementFromAction(action);

    const newQl = questionnaireLogicService.appendNodeStatement(
      currentQl,
      nodeRefId,
      statement,
    );
    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: logicDomain,
    });

    return {
      state: Object.assign({}, state, {
        [logicKey]: newQuestionnaireLogic,
        statementIdToScroll: scrollTo ? statement.id : undefined,
      }),
      statementId: statement.id,
    };
  } catch (error) {
    // eslint-disable-next-line
    return {
      state: Object.assign({}, state, {
        [logicKey]: {
          ...logicDomain,
          error,
        },
      }),
    };
  }
}

export function updateStatementActionHandler(state, action) {
  const {
    nodeRefId,
    statementId,
    outcome,
    logicMode = LOGIC_MODES.PLAIN,
    shouldResetQuota,
  } = action.payload;

  const logicKey = getLogicKeyFromMode(logicMode);

  try {
    let newQl = questionnaireLogicService.updateOutcome(
      state[logicKey].ql,
      nodeRefId,
      statementId,
      outcome,
    );

    if (shouldResetQuota) {
      newQl = questionnaireLogicService.updateRuleQuota(
        newQl,
        nodeRefId,
        statementId,
      );
    }

    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: state[logicKey],
    });

    const questions = get(state, 'surveyDetailsExtended.questions', []);
    const screeningQuestions = get(
      state,
      'surveyDetailsExtended.screeningQuestions',
      [],
    );
    const { errors: globalErrors } = state;
    const { previousNodesPerNode } = newQuestionnaireLogic;
    const { questions: updatedQuestions, questionnaireErrors } =
      getRevalidatedQuestionsAndGlobalErrors({
        questions,
        screeningQuestions,
        previousNodesPerNode: mergePreviousNodesPerNode(
          state,
          logicKey,
          previousNodesPerNode,
        ),
        globalErrors,
        filter: ({ carryForward = [], type }) =>
          Boolean(carryForward.length) || type === GROUP_LOOP_MERGE,
        surveyConfig: getSurveyConfigForValidate(state),
      });

    return Object.assign({}, state, {
      errors: questionnaireErrors,
      [logicKey]: newQuestionnaireLogic,
      surveyDetailsExtended: {
        ...state.surveyDetailsExtended,
        questions: updatedQuestions,
      },
    });
  } catch (error) {
    return Object.assign({}, state, {
      [logicKey]: {
        ...state[logicKey],
        error,
      },
    });
  }
}

export function addConditionActionHandler(state, action) {
  const {
    statementId,
    ruleId,
    referencedNode,
    context,
    logicMode = LOGIC_MODES.PLAIN,
    variableValueRefId,
    isNodeLevelVisibility,
  } = action.payload;
  let { nodeRefId } = action.payload;

  const targetLogic = getLogicKeyFromMode(logicMode);
  const contextType = get(context, 'contextType');

  if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
    nodeRefId = DISQUALIFY_PLACEHOLDER_QUESTION.id;
  }

  try {
    let newCondition;

    if (contextType === QuestionnaireConditionContext.Taxonomy) {
      const taxonomyProperty = context.property;
      const taxonomyQuestionObject = find(
        state.targeting.demographics,
        ['key', taxonomyProperty],
        {},
      );
      const taxonomyQuestionType = get(taxonomyQuestionObject, 'questionType');
      const defaultMatcher = getDefaultMatcherForDemographic(
        taxonomyProperty,
        taxonomyQuestionType,
      );
      const isNumeric =
        isNumericOpenEndedPeeqQuestionType(taxonomyQuestionType);
      const isText = isOpenEndedPeeqQuestionType(taxonomyQuestionType);
      newCondition = getTaxonomyCondition({
        matcher: defaultMatcher,
        taxonomyProperty,
        defaultValue: '',
        isNumeric,
        isText,
      });
    }

    if (contextType === QuestionnaireConditionContext.Answer) {
      if (!referencedNode) {
        throw new Error(
          'Missing referenced node from add condition action and contextType: Answer',
        );
      }
      const isNumeric = isNumericQuestionType(referencedNode.type);
      const isText = isTextQuestionType(referencedNode.type);
      const answerRefId = getDefaultConditionAnswer(referencedNode);

      const defaultMatcher = getDefaultMatcherForQuestionType(
        referencedNode.type,
        logicMode,
      );
      newCondition = getAnswerCondition({
        questionRefId: nodeRefId,
        matcher: defaultMatcher,
        isNumeric,
        isText,
        context,
        defaultValue: answerRefId,
      });
    }

    if (contextType === QuestionnaireConditionContext.Visibility) {
      if (!referencedNode) {
        throw new Error(
          'Missing referenced node from add condition action and contextType: Visibility',
        );
      }
      const value = getDefaultConditionVisibility(
        referencedNode,
        isNodeLevelVisibility,
      );
      const defaultMatcher = getDefaultMatcherForVisibility(
        isNodeLevelVisibility,
      );

      newCondition = getVisibilityCondition({
        questionRefId: context.questionId,
        matcher: defaultMatcher,
        defaultValue: value,
      });
    }

    if (contextType === QuestionnaireConditionContext.Concept) {
      const defaultMatcher = getDefaultMatcherForConcept();
      newCondition = getConceptCondition({
        matcher: defaultMatcher,
        nodeRefId: context.nodeId,
        defaultValue: '',
      });
    }

    if (contextType === QuestionnaireConditionContext.Variable) {
      newCondition = getVariableCondition({
        matcher: getDefaultMatcherForVariable(),
        variableRefId: context.variableId,
        defaultValue: variableValueRefId,
      });
    }

    const newQl = questionnaireLogicService.addCondition(
      state[targetLogic].ql,
      nodeRefId,
      statementId,
      ruleId,
      newCondition,
      false,
    );

    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: state[targetLogic],
    });

    return Object.assign({}, state, {
      [targetLogic]: newQuestionnaireLogic,
    });
  } catch (error) {
    return Object.assign({}, state, {
      [targetLogic]: {
        ...state[targetLogic],
        error,
      },
    });
  }
}

export function addRuleActionHandler(state, action) {
  const {
    nodeRefId,
    statementId,
    matcher,
    isNumeric = false,
    isText = false,
    defaultValue,
    context,
    logicMode = LOGIC_MODES.PLAIN,
    contextType,
  } = action.payload;

  const logicKey = getLogicKeyFromMode(logicMode);
  const currentQl = state[logicKey].ql;

  try {
    let newCondition;
    if (!contextType) {
      newCondition = conditionService.createCondition(null, null, null);
    } else if (contextType === QuestionnaireConditionContext.Answer) {
      newCondition = getAnswerCondition({
        questionRefId: nodeRefId,
        matcher,
        isNumeric,
        isText,
        defaultValue,
        context,
      });
    } else {
      newCondition = conditionService.createCondition(null, null, null);
    }

    const qStatements = questionnaireLogicService.findNodeStatementsByNodeId(
      currentQl,
      nodeRefId,
    );

    const statement = find(qStatements.statements, ['id', statementId]);
    const { rule } = statement;
    const isRuleBranched = rule.conditions.some((condition) =>
      has(condition, 'operator'),
    );

    const newRule = new Rule({
      operator: Operator.Any,
      conditions: [newCondition],
    });
    let newQl;
    if (isRuleBranched) {
      newQl = questionnaireLogicService.addCondition(
        currentQl,
        nodeRefId,
        statementId,
        rule.id,
        newRule,
        false,
      );
    } else {
      newQl = questionnaireLogicService.addRule(
        currentQl,
        nodeRefId,
        statementId,
        newRule,
      );
    }
    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: state[logicKey],
    });

    return Object.assign({}, state, {
      [logicKey]: newQuestionnaireLogic,
    });
  } catch (error) {
    return Object.assign({}, state, {
      [logicKey]: {
        ...state[logicKey],
        error,
      },
    });
  }
}

export function updateRuleOperatorActionHandler(state, action) {
  const {
    statementId,
    ruleId,
    value,
    logicMode = LOGIC_MODES.PLAIN,
  } = action.payload;
  let { nodeRefId } = action.payload;

  if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
    nodeRefId = DISQUALIFY_PLACEHOLDER_QUESTION.id;
  }

  const targetLogic = getLogicKeyFromMode(logicMode);
  try {
    const newQl = questionnaireLogicService.updateRuleOperator(
      state[targetLogic].ql,
      nodeRefId,
      statementId,
      ruleId,
      value,
    );
    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: state[targetLogic],
    });

    return Object.assign({}, state, {
      [targetLogic]: newQuestionnaireLogic,
    });
  } catch (error) {
    return Object.assign({}, state, {
      [targetLogic]: {
        ...state[targetLogic],
        error,
      },
    });
  }
}

export function updateRuleQuotaActionHandler(state, action) {
  const {
    statementId,
    quota,
    outcome,
    logicMode = LOGIC_MODES.PLAIN,
  } = action.payload;
  let { nodeRefId } = action.payload;

  if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
    nodeRefId = DISQUALIFY_PLACEHOLDER_QUESTION.id;
  }

  const targetLogic = getLogicKeyFromMode(logicMode);

  try {
    let newQl = questionnaireLogicService.updateRuleQuota(
      state[targetLogic].ql,
      nodeRefId,
      statementId,
      quota,
    );

    if (outcome) {
      newQl = questionnaireLogicService.updateOutcome(
        newQl,
        nodeRefId,
        statementId,
        outcome,
      );
    }

    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: state[targetLogic],
    });

    return Object.assign({}, state, {
      [targetLogic]: newQuestionnaireLogic,
    });
  } catch (error) {
    return Object.assign({}, state, {
      [targetLogic]: {
        ...state[targetLogic],
        error,
      },
    });
  }
}

function generateOutcome({
  outcomeType,
  outcomeAction,
  nextQuestionRefId,
  outcomeReason,
}) {
  const outcomeProps = [
    OutcomeType.Variable,
    OutcomeType.Answer,
    OutcomeType.Node,
  ].includes(outcomeType)
    ? {
        type: outcomeType,
        actions: [outcomeAction],
      }
    : {
        type: outcomeType,
        params: new NextNode({
          id: nextQuestionRefId || FinalNode,
        }),
      };

  if (outcomeReason) {
    outcomeProps.reason = outcomeReason;
  }

  return new Outcome(outcomeProps);
}

export function addUnconditionalHideAnswerActionHandler(state, action) {
  const {
    nodeRefId,
    valueRefId,
    valueType,
    logicMode = LOGIC_MODES.PLAIN,
    hideLogicMode = HIDE_ANSWER_LOGIC_MODE.REMOVE_RULES,
  } = action.payload;
  const logicKey = getLogicKeyFromMode(logicMode);
  const logicDomain = state[logicKey];
  const currentQl = logicDomain.ql;
  const groupRefId = state.surveyDetailsExtended.questions.find(
    (node) => node.refId === nodeRefId,
  )?.groupRefId;

  try {
    const keepRules = hideLogicMode === HIDE_ANSWER_LOGIC_MODE.KEEP_RULES;

    // A hidden answer stays in the questionnaire, only its logic goes.
    // Keep rules leaves conditions that reference the answer in place,
    // including on carried-forward copies.
    let newQl = keepRules
      ? getCarriedForwardedOptionsByRefId({
          nodes: state.surveyDetailsExtended.questions,
          initialObject: {
            nodeRefId,
            valueRefId,
            valueType,
          },
        }).reduce((ql, item) => {
          const isColumnType =
            item.valueType === ActionVisibilityValueType.Column;

          return questionnaireLogicService.hideAnswerKeepingLogic(
            ql,
            item.nodeRefId,
            isColumnType ? null : item.valueRefId,
            isColumnType ? item.valueRefId : null,
          );
        }, currentQl)
      : qlRemoveLogicOfItemAndCopies({
          ql: currentQl,
          nodes: state.surveyDetailsExtended.questions,
          nodeRefId,
          valueRefId,
          valueType,
          updateQuestionnaireNode: false,
        });

    // A hidden answer keeps its screening rules.
    newQl = qlKeepScreeningRules({
      node: findQuestionByRefId(
        state.surveyDetailsExtended.questions,
        nodeRefId,
      ),
      ql: newQl,
      previousQl: currentQl,
    });

    const newOutcome = getDefaultStatementWithOutcomeAction({
      nodeRefId,
      valueRefId,
      valueType,
    });
    newOutcome.outcomeAction.type = OutcomeActionType.Hide;

    const newStatement = new Statement({
      outcome: generateOutcome(newOutcome),
    });

    newQl = questionnaireLogicService.appendNodeStatement(
      newQl,
      nodeRefId,
      newStatement,
    );

    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: logicDomain,
      shouldUpdateVersion: false,
    });
    const {
      previousNodesPerNode,
      answersWithLogicFromOutcomes,
      answersWithLogicFromOutcomesHide,
      nodesWithLogicFromOutcomesShow,
      hiddenNodes,
    } = newQuestionnaireLogic;

    const node = findQuestionByRefId(
      state.surveyDetailsExtended.questions,
      nodeRefId,
    );

    const nodeErrors = getNodeErrors({
      node,
      nodes: state.surveyDetailsExtended.questions,
      previousNodesPerNode,
      answersWithLogicFromOutcomes,
      answersWithLogicFromOutcomesHide,
      nodesWithLogicFromOutcomesShow,
      hiddenNodes,
    });

    const newGlobalErrors = updateQuestionnaireErrors({
      globalErrors: state.errors,
      nodeRefId,
      groupRefId,
      nodeErrors,
      nodeType: node.type,
    });

    const newState = Object.assign({}, state, {
      [logicKey]: newQuestionnaireLogic,
      errors: newGlobalErrors,
      surveyDetailsExtended: {
        ...state.surveyDetailsExtended,
        questions: state.surveyDetailsExtended.questions.map((question) =>
          question.refId === nodeRefId
            ? { ...question, errors: nodeErrors }
            : question,
        ),
      },
    });

    return validateQuotaAndUpdateState(newState);
  } catch (error) {
    return Object.assign({}, state, {
      [logicKey]: {
        ...logicDomain,
        error,
      },
    });
  }
}

export function removeUnconditionalHideAnswerActionHandler(state, action) {
  const {
    nodeRefId,
    valueRefId,
    valueType,
    logicMode = LOGIC_MODES.PLAIN,
  } = action.payload;
  const logicKey = getLogicKeyFromMode(logicMode);
  const logicDomain = state[logicKey];
  const currentQl = logicDomain.ql;
  const groupRefId = state.surveyDetailsExtended.questions.find(
    (node) => node.refId === nodeRefId,
  )?.groupRefId;

  try {
    // Restore only drops the hide outcome: rules kept on the hidden answer
    // (Keep rules) must survive, and Delete rules already pruned the rest.
    let newQl = questionnaireLogicService.removeAnswerWithLogicFromOutcome(
      currentQl,
      nodeRefId,
      valueRefId,
    );
    const isColumnType = valueType === ActionVisibilityValueType.Column;

    const node = findQuestionByRefId(
      state.surveyDetailsExtended.questions,
      nodeRefId,
    );
    // An unhidden answer keeps its screening rules.
    newQl = qlKeepScreeningRules({ node, ql: newQl, previousQl: currentQl });

    let newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: logicDomain,
      shouldUpdateVersion: false,
    });

    // On a screening node only the unhidden answer gets a missing quota; an
    // answer whose quota was cleared stays missing.
    const isScreeningAnswer = node?.isScreening && !isColumnType;
    newQl = handleMissingQuotasForNode(
      {
        ...state,
        [logicKey]: newQuestionnaireLogic,
      },
      node,
      logicKey,
      isScreeningAnswer
        ? {
            isEligibleItem: (item) =>
              item.answerRefId === valueRefId &&
              getQuotaEligibilityForNode({ node, ql: newQl })(item),
          }
        : {},
    );

    newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: logicDomain,
      shouldUpdateVersion: false,
    });

    const {
      previousNodesPerNode,
      answersWithLogicFromOutcomes,
      answersWithLogicFromOutcomesHide,
      nodesWithLogicFromOutcomesShow,
      hiddenNodes,
    } = newQuestionnaireLogic;

    const nodeErrors = getNodeErrors({
      node,
      nodes: state.surveyDetailsExtended.questions,
      previousNodesPerNode,
      answersWithLogicFromOutcomes,
      answersWithLogicFromOutcomesHide,
      nodesWithLogicFromOutcomesShow,
      hiddenNodes,
    });

    const newGlobalErrors = updateQuestionnaireErrors({
      globalErrors: state.errors,
      nodeRefId,
      groupRefId,
      nodeErrors,
      nodeType: node.type,
    });

    const newState = Object.assign({}, state, {
      [logicKey]: newQuestionnaireLogic,
      errors: newGlobalErrors,
      surveyDetailsExtended: {
        ...state.surveyDetailsExtended,
        questions: state.surveyDetailsExtended.questions.map((question) =>
          question.refId === nodeRefId
            ? { ...question, errors: nodeErrors }
            : question,
        ),
      },
    });

    // Node validation replaced the node's errors; put the quota errors back.
    return logicMode === LOGIC_MODES.PLAIN
      ? validateQuotaAndUpdateState(newState)
      : newState;
  } catch (error) {
    return Object.assign({}, state, {
      [logicKey]: {
        ...logicDomain,
        error,
      },
    });
  }
}

// `isEligibleItem`, when given, narrows which items get a missing quota.
export function handleMissingQuotasForNode(
  questionnairePageState,
  node,
  targetLogic,
  { isEligibleItem } = {},
) {
  const quotasMinimalMap = makeSelectQuestionQuotasMinimalMap({
    questionnairePage: questionnairePageState,
  });
  let newQl = questionnairePageState[targetLogic].ql;
  if (NODE_TYPES_SUPPORT_SIMPLE_QUOTAS.includes(node.type)) {
    const mustUpdateQuota = Boolean(
      quotasMinimalMap[node.refId]?.simpleQuotasPresent,
    );
    if (mustUpdateQuota) {
      newQl = qlAddMissingQuotasForNode({
        node,
        ql: newQl,
        unconditionallyHiddenItems: getUnconditionallyHiddenItems(
          questionnairePageState,
          targetLogic,
        ),
        isEligibleItem,
      });
    }
  }

  return newQl;
}
export function toggleVisibilityActionHandler(state, action) {
  const { surveyDetailsExtended, errors } = state;
  const { questions = [], screeningQuestions = [] } = surveyDetailsExtended;
  const {
    nodeRefId,
    isNodeHidden,
    hasMentions,
    logicMode = LOGIC_MODES.PLAIN,
  } = action.payload;
  const logicKey = getLogicKeyFromMode(logicMode);
  const logicDomain = state[logicKey];

  try {
    let nodes = questions;

    if (!isNodeHidden && hasMentions) {
      nodes = getNodesWithoutMentionsOnRemovedNode(nodes, nodeRefId);
    }

    const newQl = isNodeHidden
      ? questionnaireLogicService.removeOutcomeActions(
          logicDomain.ql,
          [OutcomeType.Node],
          (outcomeAction) =>
            !(
              outcomeAction.type === OutcomeActionType.Hide &&
              outcomeAction.valueType === ActionVisibilityValueType.Node &&
              outcomeAction.targetId === nodeRefId
            ),
        )
      : questionnaireLogicService.hideNodeWithLogic(logicDomain.ql, nodeRefId);

    const newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: logicDomain,
      shouldUpdateVersion: false,
    });
    const {
      previousNodesPerNode,
      answersWithLogicFromOutcomes,
      answersWithLogicFromOutcomesHide,
      nodesWithLogicFromOutcomesShow,
      hiddenNodes,
    } = newQuestionnaireLogic;

    const { newNodes, globalErrors } = getRevalidatedNodesAndGlobalErrors({
      nodes,
      errors,
      screeningQuestions,
      previousNodesPerNode,
      answersWithLogicFromOutcomes,
      answersWithLogicFromOutcomesHide,
      nodesWithLogicFromOutcomesShow,
      hiddenNodes,
      surveyConfig: getSurveyConfigForValidate(state),
    });

    const newState = Object.assign({}, state, {
      errors: globalErrors,
      [logicKey]: newQuestionnaireLogic,
      surveyDetailsExtended: {
        ...surveyDetailsExtended,
        questions: newNodes,
      },
    });

    // Node validation replaced the nodes' errors; put the quota errors back.
    return logicMode === LOGIC_MODES.PLAIN
      ? validateQuotaAndUpdateState(newState)
      : newState;
  } catch (error) {
    // eslint-disable-next-line
    return {
      state: Object.assign({}, state, {
        [logicKey]: {
          ...logicDomain,
          error,
        },
      }),
    };
  }
}

export function validateAllNodesActionHandler(state, action) {
  const { surveyDetailsExtended, errors } = state;
  const { questions = [], screeningQuestions = [] } = surveyDetailsExtended;
  const { logicMode = LOGIC_MODES.PLAIN } = action.payload || {};
  const logicKey = getLogicKeyFromMode(logicMode);
  const questionnaireLogic = state[logicKey];

  const {
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = questionnaireLogic;

  const { newNodes, globalErrors } = getRevalidatedNodesAndGlobalErrors({
    nodes: questions,
    errors,
    screeningQuestions,
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
    surveyConfig: getSurveyConfigForValidate(state),
  });

  const newState = Object.assign({}, state, {
    errors: globalErrors,
    surveyDetailsExtended: {
      ...surveyDetailsExtended,
      questions: newNodes,
    },
  });

  // Node validation replaces each node's errors; the quota pass puts the quota
  // errors (screening quotas included) back.
  return logicMode === LOGIC_MODES.PLAIN
    ? validateQuotaAndUpdateState(newState)
    : newState;
}

function qlAddMissingQuotasForNode({
  node,
  ql,
  unconditionallyHiddenItems,
  isEligibleItem = getQuotaEligibilityForNode({
    node,
    ql,
    unconditionallyHiddenItems,
  }),
}) {
  if (!NODE_TYPES_SUPPORT_SIMPLE_QUOTAS.includes(node.type)) return ql;

  return addQuotaRulesForSpecificAnswersColumns({
    questionRefId: node.refId,
    questionType: node.type,
    ...getQuotaEligibleOptions({
      node,
      unconditionallyHiddenItems,
      isEligibleItem,
    }),
    totalQuota: 0,
    ql,
    isScreening: Boolean(node.isScreening),
  });
}
